import { zodResolver } from "@hookform/resolvers/zod";
import { MenuItem, Button, TextField } from "@mui/material";
import { MapPinLineIcon, CaretDownIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { type ReactNode, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";

import { usePreviousHotelSearchData } from "@/app/lib/hooks/usePreviousSearchData";
import { useSnackbarRHFErrors } from "@/app/lib/hooks/useSnackbarRHFErrors";
import {
  HotelSearchTrackingEvents,
  rudderAnalytics,
  type SearchSource,
} from "@/app/lib/rudderstack";
import { getDateString } from "@/app/lib/utils/dates";
import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { ResetButton } from "@/components/ResetButton/ResetButton";
import CheckInSelection from "@/features/hotels/components/hotels/CheckInSelection";
import DestinationSearch from "@/features/hotels/components/hotels/DestinationSearch";
import { getValidationSchema } from "@/features/hotels/components/hotels/validation";
import VisitorsSelection from "@/features/hotels/components/hotels/VisitorsSelections";
import type {
  THotelFormFieldValues,
  THotelFormValues,
} from "@/features/hotels/types/types";
import { initialHotelsValues } from "@/features/hotels/utils/initialHotelsValues";
import { TravelPurpose } from "@/types/enums";

enum DrawerComponent {
  DestinationSearch = "DestinationSearch",
  CheckInSelection = "CheckInSelection",
  VisitorsSelection = "VisitorsSelection",
}

type Props = {
  submitButtonContent: ReactNode;
  initialValues?: THotelFormValues | null;
  showErrorsInline?: boolean;
  onSubmit: (submittedValues: THotelFormValues) => void;
  analyticsSource: SearchSource;
};

export const HotelsCalculator = ({
  submitButtonContent,
  initialValues,
  showErrorsInline,
  onSubmit,
  analyticsSource,
}: Props) => {
  const [drawerComponent, setDrawerComponent] =
    useState<DrawerComponent | null>(null);
  const { previousSearchData } = usePreviousHotelSearchData();

  const t = useTranslations();

  const schema = useMemo(() => getValidationSchema(t), [t]);

  const methods = useForm<THotelFormFieldValues, unknown, THotelFormValues>({
    defaultValues: initialValues || previousSearchData,
    mode: showErrorsInline ? "onBlur" : "onSubmit",
    reValidateMode: showErrorsInline ? "onChange" : "onSubmit",
    resolver: zodResolver(schema),
  });

  const {
    handleSubmit,
    getValues,
    setValue,
    reset,
    formState: { errors, touchedFields, submitCount },
    control,
  } = methods;

  useSnackbarRHFErrors<THotelFormFieldValues>({
    disabled: showErrorsInline,
    errors,
    touched: touchedFields,
    submitCount,
  });

  const [dates, destination, rooms, travelPurpose] = useWatch({
    control,
    name: ["dates", "destination", "rooms", "travelPurpose"],
  });

  const checkInValue = dayjs(dates.checkIn).format("ddd, D MMM");
  const checkOutValue = dates.checkOut
    ? dayjs(dates.checkOut).format("ddd, D MMM")
    : "_";

  const travelPurposeOptions = [
    {
      value: TravelPurpose.LEISURE,
      label: t("hotels.search.leisure"),
    },
    {
      value: TravelPurpose.BUSINESS,
      label: t("hotels.search.business"),
    },
  ];

  return (
    <FormProvider {...methods}>
      <form
        className="w-full"
        noValidate
        onSubmit={handleSubmit((data) => {
          onSubmit(data);
        })}
        acceptCharset="ISO-8859-1"
      >
        <div className="mb-4 flex flex-col gap-2">
          <div className="relative flex flex-col gap-2">
            <TextField
              fullWidth
              value={destination?.name || ""}
              error={showErrorsInline && !!errors.destination?.name}
              helperText={showErrorsInline && errors.destination?.name?.message}
              name="destination.name"
              label={
                <div className="flex items-center gap-2">
                  <MapPinLineIcon size={24} />
                  <span>{t("hotels.search.destination")}</span>
                </div>
              }
              onClick={() => {
                rudderAnalytics.track(
                  HotelSearchTrackingEvents.USER_CLICKED_HOTEL_DESTINATION_INPUT,
                  {
                    source: analyticsSource,
                  },
                );
                setDrawerComponent(DrawerComponent.DestinationSearch);
              }}
              slotProps={{
                input: {
                  endAdornment: destination ? (
                    <ResetButton
                      onClick={() => {
                        setValue(
                          "destination",
                          initialHotelsValues.destination,
                        );
                      }}
                      aria-label={t("hotels.search.resetDestination")}
                    />
                  ) : null,
                },
              }}
            />
          </div>
          <TextField
            fullWidth
            name="dates"
            label={t("hotels.search.checkInAndCheckOut")}
            value={`${checkInValue} - ${checkOutValue}`}
            onClick={() => {
              rudderAnalytics.track(
                HotelSearchTrackingEvents.USER_CLICKED_HOTEL_CALENDAR_INPUT,
                {
                  source: analyticsSource,
                },
              );
              setDrawerComponent(DrawerComponent.CheckInSelection);
            }}
          />
          <TextField
            fullWidth
            name="rooms"
            label={t("hotels.search.visitors")}
            value={t("hotels.search.visitorsSumWithTypes", {
              adultsCount: rooms.reduce((acc, room) => acc + room.adults, 0),
              childrenCount: rooms.reduce(
                (acc, room) => acc + room.children,
                0,
              ),
            })}
            slotProps={{
              input: {
                endAdornment: (
                  <CaretDownIcon
                    className="shrink-0"
                    color="black"
                    size={20}
                    weight="bold"
                  />
                ),
              },
            }}
            onClick={(e) => {
              e.preventDefault();
              rudderAnalytics.track(
                HotelSearchTrackingEvents.USER_CLICKED_HOTEL_TRAVELLERS_INPUT,
                {
                  source: analyticsSource,
                },
              );
              setDrawerComponent(DrawerComponent.VisitorsSelection);
            }}
          />
          <TextField
            select
            fullWidth
            name="travelPurpose"
            label={t("hotels.search.travelPurpose")}
            value={travelPurpose}
            slotProps={{
              input: {
                endAdornment: (
                  <CaretDownIcon
                    className="shrink-0"
                    color="black"
                    size={20}
                    weight="bold"
                  />
                ),
              },
            }}
            onChange={(e) => {
              setValue("travelPurpose", e.target.value as TravelPurpose);
            }}
          >
            {travelPurposeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </div>
        <Button fullWidth variant="contained" type="submit">
          {submitButtonContent}
        </Button>
      </form>

      {/* we're not using open prop and must mount fresh instance of the component
      because otherwise initial opening animation breaks scroll when mobile keyboard is present */}
      {drawerComponent ? (
        <SwipeableDrawer
          hasFullHeight={
            !!drawerComponent &&
            drawerComponent !== DrawerComponent.VisitorsSelection
          }
          onClose={() => setDrawerComponent(null)}
          open
          disableRestoreFocus
          heightOffset={24}
        >
          {drawerComponent === DrawerComponent.DestinationSearch && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerComponent(null)}
                title={t("hotels.search.destinationSelect")}
              />
              <DestinationSearch
                analyticsSource={analyticsSource}
                onClose={() => setDrawerComponent(null)}
              />
            </>
          )}
          {drawerComponent === DrawerComponent.CheckInSelection && (
            <CheckInSelection
              initialValues={getValues()}
              onConfirm={(data) => {
                rudderAnalytics.track(
                  HotelSearchTrackingEvents.USER_CHANGED_HOTEL_CALENDAR_DATE,
                  {
                    source: analyticsSource,
                    dateFrom: getDateString(data.dates.checkIn),
                    dateTo: getDateString(data.dates.checkOut),
                  },
                );
                reset(data);
                setDrawerComponent(null);
              }}
            />
          )}
          {drawerComponent === DrawerComponent.VisitorsSelection && (
            <>
              <DrawerTopBar
                onCloseClick={() => setDrawerComponent(null)}
                title={t("hotels.search.visitors")}
              />
              <VisitorsSelection
                onConfirm={(data) => {
                  rudderAnalytics.track(
                    HotelSearchTrackingEvents.USER_CHANGED_HOTEL_TRAVELLERS,
                    {
                      source: analyticsSource,
                      // for now we are not supporting multiple rooms booking so we send only first room data
                      travellers: data.rooms[0],
                    },
                  );
                  setDrawerComponent(null);
                }}
              />
            </>
          )}
        </SwipeableDrawer>
      ) : null}
    </FormProvider>
  );
};
